class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.onVariantChange = this.onVariantChange.bind(this);
    this.addEventListener("change", this.onVariantChange);
    this.renderController = null;
    this.renderRequestId = 0;
  }

  onVariantChange() {
    this.updateOptions();
    this.updateOptionsParent();
    this.updateMasterId();
    this.updateVariantStatuses();
    this.getPreOrderVariantData();

    if (!this.currentVariant) {
      this.abortPendingRender();
      this.setUnavailable();
      this.updateNotifyMe(null);
      this.updatePickupAvailability(null);
      this.dispatchVariantChange(null, "unavailable");
      return;
    }

    this.updateMedia();
    this.updateURL();
    this.updateVariantInput();

    const purchaseState = this.getPurchaseState(this.currentVariant);
    this.updatePurchaseControls(this.currentVariant, purchaseState);
    this.updateNotifyMe(this.currentVariant);
    this.updatePickupAvailability(this.currentVariant);
    this.dispatchVariantChange(this.currentVariant, purchaseState);
    this.renderProductInfo(this.currentVariant);
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value
    );
  }

  updateOptionsParent() {
    const wrappers = this.querySelectorAll(".product-form__input--dropdown");
    wrappers.forEach((wrapper, index) => {
      const selectedValue = wrapper.querySelector(".form__label span");
      if (selectedValue && this.options[index] !== undefined) {
        selectedValue.textContent = this.options[index];
      }
    });
  }

  updateMasterId() {
    const variants = this.getVariantData();
    this.currentVariant = variants.find((variant) => {
      if (!Array.isArray(variant.options)) return false;
      return variant.options.every(
        (option, index) => this.options[index] === option
      );
    });
  }

  updateMedia() {
    if (!this.currentVariant?.featured_media) return;

    const mediaGallery = document.getElementById(
      `MediaGallery-${this.dataset.section}`
    );
    if (!mediaGallery || typeof mediaGallery.setActiveMedia !== "function") {
      return;
    }

    mediaGallery.setActiveMedia(
      `${this.dataset.section}-${this.currentVariant.featured_media.id}`,
      true
    );
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", this.currentVariant.id);
      window.history.replaceState({}, "", url.toString());
    } catch (error) {
      window.history.replaceState(
        {},
        "",
        `${this.dataset.url}?variant=${this.currentVariant.id}`
      );
    }
  }

  updateVariantInput() {
    const productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
    const primaryForm = document.getElementById(`product-form-${this.dataset.section}`);
    const productForms = new Set();

    if (primaryForm) productForms.add(primaryForm);
    productInfo
      ?.querySelectorAll('#product-form-installment, [id^="product-form-installment-"]')
      .forEach((form) => productForms.add(form));

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input) return;
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  getSelectedValue(wrapper) {
    const selected = wrapper.querySelector(":checked");
    return selected ? selected.value : null;
  }

  updateVariantStatuses() {
    const inputWrappers = [...this.querySelectorAll(".product-form__input")];
    if (inputWrappers.length < 2) return;

    const variants = this.getVariantData();

    inputWrappers.forEach((wrapper, index) => {
      if (index === 0) return;

      const selectedPreviousValues = inputWrappers
        .slice(0, index)
        .map((previousWrapper) => this.getSelectedValue(previousWrapper));

      const availableValues = variants
        .filter((variant) => {
          if (!variant.available || !Array.isArray(variant.options)) return false;
          return selectedPreviousValues.every(
            (selectedValue, optionIndex) =>
              selectedValue === null || variant.options[optionIndex] === selectedValue
          );
        })
        .map((variant) => variant.options[index]);

      const optionInputs = [
        ...wrapper.querySelectorAll('input[type="radio"], option'),
      ];
      this.setInputAvailability(optionInputs, availableValues);
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      const value = input.getAttribute("value") || "";
      const isAvailable = listOfAvailableOptions.includes(value);
      const unavailableTemplate =
        window.variantStrings?.unavailableWithOption ||
        "[value] - Unavailable";

      input.textContent = isAvailable
        ? value
        : unavailableTemplate.replace("[value]", value);
      input.dataset.optionAvailable = String(isAvailable);
    });
  }

  updatePickupAvailability(variant = this.currentVariant) {
    const pickUpAvailability = document.querySelector(
      `pickup-availability[data-section="${this.dataset.section}"]`
    );
    if (!pickUpAvailability) return;

    if (
      variant?.available &&
      typeof pickUpAvailability.fetchAvailability === "function"
    ) {
      pickUpAvailability.fetchAvailability(variant.id);
    } else {
      pickUpAvailability.removeAttribute("available");
      pickUpAvailability.innerHTML = "";
    }
  }

  updateNotifyMe(variant = this.currentVariant) {
    const wrapper = document.getElementById(
      `notify__me--available-${this.dataset.section}`
    );
    if (!wrapper) return;

    const shouldShow = Boolean(variant && !variant.available);
    wrapper.hidden = !shouldShow;
    wrapper.classList.toggle("no-js-inline", !shouldShow);
    wrapper.setAttribute("aria-hidden", String(!shouldShow));

    const productUrlField = document.getElementById(
      `soldout__product_url--${this.dataset.section}`
    );
    if (productUrlField && variant) {
      const origin = this.dataset.origin || window.location.origin;
      productUrlField.value = `${origin}${this.dataset.url}?variant=${variant.id}`;
    }
  }

  async renderProductInfo(variant = this.currentVariant) {
    if (!variant) return;

    this.abortPendingRender();
    this.renderController = new AbortController();
    const requestId = ++this.renderRequestId;
    const requestedVariantId = String(variant.id);
    const inventoryDestination = document.getElementById(
      `inventory__stock--${this.dataset.section}`
    );
    if (inventoryDestination) {
      inventoryDestination.hidden = true;
      inventoryDestination.setAttribute("aria-busy", "true");
    }

    try {
      const response = await fetch(
        `${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`,
        { signal: this.renderController.signal }
      );

      if (!response.ok) {
        throw new Error(`Variant section request failed: ${response.status}`);
      }

      const responseText = await response.text();

      if (
        requestId !== this.renderRequestId ||
        String(this.currentVariant?.id) !== requestedVariantId
      ) {
        return;
      }

      const html = new DOMParser().parseFromString(responseText, "text/html");
      const elementIds = [
        `price-${this.dataset.section}`,
        `inventory__stock--${this.dataset.section}`,
        `barcode__${this.dataset.section}`,
        `sku__${this.dataset.section}`,
      ];

      elementIds.forEach((id) => {
        const destination = document.getElementById(id);
        const source = html.getElementById(id);
        if (!source || !destination) return;
        destination.innerHTML = source.innerHTML;
        destination.hidden = false;
        destination.removeAttribute("aria-busy");
      });

      const price = document.getElementById(`price-${this.dataset.section}`);
      price?.classList.remove("no-js-inline");

      this.syncAddButtonFromServer(html);
      const purchaseState = this.getPurchaseState(this.currentVariant);
      this.updatePurchaseControls(this.currentVariant, purchaseState, false);
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Unable to refresh variant information", error);
        if (String(this.currentVariant?.id) === requestedVariantId) {
          inventoryDestination?.removeAttribute("aria-busy");
        }
      }
    }
  }

  abortPendingRender() {
    if (this.renderController) {
      this.renderController.abort();
      this.renderController = null;
    }
  }

  getProductForm() {
    return document.getElementById(`product-form-${this.dataset.section}`);
  }

  getProductFormElement() {
    return this.getProductForm()?.closest("product-form") || null;
  }

  getPreorderEnabled() {
    return this.getProductFormElement()?.dataset.preorderEnabled === "true";
  }

  getPurchaseState(variant = this.currentVariant) {
    if (!variant) return "unavailable";
    if (!variant.available) return "soldout";

    const preorderData = this.getPreOrderVariantData();
    const variantInventory = preorderData[String(variant.id)] || {};
    const quantity = Number(variantInventory.qty);
    const inventoryPolicy = variantInventory.inventory_policy;

    if (
      this.getPreorderEnabled() &&
      Number.isFinite(quantity) &&
      quantity <= 0 &&
      inventoryPolicy === "continue"
    ) {
      return "preorder";
    }

    return "available";
  }

  getAddButtonText(addButton) {
    return addButton?.querySelector(".cart--button-text") || addButton || null;
  }

  updatePurchaseControls(
    variant = this.currentVariant,
    purchaseState = this.getPurchaseState(variant),
    updateText = true
  ) {
    const productForm = this.getProductForm();
    if (!productForm) return;

    const addButton = productForm.querySelector('[name="add"]');
    if (!addButton) return;

    const addButtonText = this.getAddButtonText(addButton);
    const isDisabled = purchaseState === "soldout" || purchaseState === "unavailable";

    addButton.disabled = isDisabled;
    addButton.dataset.variantAvailable = String(!isDisabled);
    addButton.dataset.variantId = variant?.id || "";

    if (updateText && addButtonText) {
      const buttonText = {
        available: window.variantStrings?.addToCart || "Add to cart",
        preorder: window.variantStrings?.preorder || "Preorder",
        soldout: window.variantStrings?.soldOut || "Sold out",
        unavailable: window.variantStrings?.unavailable || "Unavailable",
      }[purchaseState];

      addButtonText.textContent = buttonText;
    }

    const dynamicCheckout = productForm.querySelector(
      ".product-form__dynamic-checkout, .shopify-payment-button"
    );
    if (dynamicCheckout) dynamicCheckout.hidden = isDisabled;
  }

  syncAddButtonFromServer(html) {
    const destinationForm = this.getProductForm();
    const sourceForm = html.getElementById(`product-form-${this.dataset.section}`);
    if (!destinationForm || !sourceForm) return;

    const destinationButton = destinationForm.querySelector('[name="add"]');
    const sourceButton = sourceForm.querySelector('[name="add"]');
    if (!destinationButton || !sourceButton) return;

    destinationButton.innerHTML = sourceButton.innerHTML;
    destinationButton.disabled = sourceButton.disabled;
  }

  setUnavailable() {
    this.updatePurchaseControls(null, "unavailable");
    const price = document.getElementById(`price-${this.dataset.section}`);
    price?.classList.add("no-js-inline");
    const inventory = document.getElementById(
      `inventory__stock--${this.dataset.section}`
    );
    if (inventory) {
      inventory.hidden = true;
      inventory.removeAttribute("aria-busy");
    }
  }

  dispatchVariantChange(variant, purchaseState) {
    this.dispatchEvent(
      new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          sectionId: this.dataset.section,
          variant,
          purchaseState,
        },
      })
    );
  }

  getVariantData() {
    if (this.variantData) return this.variantData;

    const variantDataElement = this.querySelector("[data-variant]");
    if (!variantDataElement) return [];

    try {
      this.variantData = JSON.parse(variantDataElement.textContent);
    } catch (error) {
      console.error("Unable to parse variant data", error);
      this.variantData = [];
    }

    return this.variantData;
  }

  getPreOrderVariantData() {
    if (this.PreorderVariantData) return this.PreorderVariantData;

    const preorderDataElement = this.querySelector("[data-preorder]");
    if (!preorderDataElement) return {};

    try {
      this.PreorderVariantData = JSON.parse(preorderDataElement.textContent);
    } catch (error) {
      console.error("Unable to parse preorder variant data", error);
      this.PreorderVariantData = {};
    }

    return this.PreorderVariantData;
  }
}

if (!customElements.get("variant-selects")) {
  customElements.define("variant-selects", VariantSelects);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
    this.fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.onColorSwatches();
  }

  onColorSwatches() {
    if (!this.dataset.colorSwatch) return;

    const variantList = this.getVariantData();
    const swatchButtonList = this.querySelectorAll(
      `fieldset.variant--swatch-${this.dataset.colorSwatch} > label`
    );

    const result = Object.values(
      variantList.reduce((total, item) => {
        const key = `${item[this.dataset.colorSwatch]}`;
        if (!total[key]) total[key] = { ...item, count: 1 };
        else total[key].count += 1;
        return total;
      }, {})
    );

    swatchButtonList.forEach((button) => {
      result.forEach((data) => {
        if (data[this.dataset.colorSwatch] !== button.dataset.value) return;

        if (
          this.dataset.colorSwatchStyle === "image" &&
          data.featured_image?.src
        ) {
          button.setAttribute(
            "style",
            `--swatch-background-image: url(${data.featured_image.src}); --swatch-background-color: ${data[this.dataset.colorSwatch]};`
          );
        } else {
          button.setAttribute(
            "style",
            `--swatch-background-color: ${data[this.dataset.colorSwatch]};`
          );
        }
      });
    });
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    listOfOptions.forEach((input) => {
      const isAvailable = listOfAvailableOptions.includes(
        input.getAttribute("value")
      );
      input.classList.toggle("disabled", !isAvailable);
      input.setAttribute("aria-disabled", String(!isAvailable));
      input.dataset.optionAvailable = String(isAvailable);
    });
  }

  updateOptions() {
    this.options = this.fieldsets.map((fieldset) => {
      const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
      return checkedRadio ? checkedRadio.value : null;
    });
  }

  updateOptionsParent() {
    this.fieldsets.forEach((fieldset, index) => {
      const selectedValue = fieldset.querySelector(".form__label span");
      if (selectedValue && this.options[index] !== null) {
        selectedValue.textContent = this.options[index];
      }
    });
  }
}

if (!customElements.get("variant-radios")) {
  customElements.define("variant-radios", VariantRadios);
}
